// ── SPmtForm ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmt, fmtDate } from "../../../shared/format";
import { Btn, Card, FInput, FInputNum, FSelect, Field, Icon, Modal, ReadOnlyForm, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { useFreshForm } from "../../../shared/hooks";
import { NotesPanel } from "../components/NotesPanel";
import { ScheduledPmtLinesPanel } from "../components/ScheduledPmtLinesPanel";
import { useApp } from "../../../shared/appContext";

export const SPmtForm = ({ initial, loanid, wide, readOnly }) => {
    const { api, role, view, modal, setModal, closeModal, setConfirm, saving, error, saveRec, audit } = useApp();
    const blank = {
      scheduleddate:"", loanid,
      origscheduledprincipal:"", origscheduledinterest:"",
      origfeetype:"", origscheduledfee:"",
      origothertype:"", origscheduledother:"",
      Origpassthroughtype:"", origscheduledpassthrough:"",
      origscheduledpik:"",
      scheduledprincipal:"", scheduledinterest:"",
      feetype:"", scheduledfee:"",
      othertype:"", scheduledother:"",
      passthroughtype:"", scheduledpassthrough:"",
      scheduledpik:"",
      unscheduledpayment:false
    };
    // ── Returning to this scheduled payment after a child record was saved ──
    //
    // Two things have to happen when a received payment is added, edited or
    // deleted from inside this modal, and only one of them is obvious:
    //
    //   1. Put the user back where they were — this scheduled payment.
    //   2. Tell the page UNDERNEATH that its data moved. That page is still
    //      mounted behind the modal, holding the rows it fetched before any of
    //      this happened. Without step 2 the modal closes onto figures that are
    //      quietly out of date, which is worse than showing nothing.
    //
    // `modal.onDone` is the page's reload, handed in when this modal was opened.
    // It is carried onto the reopened modal as well, so an edit made from THERE
    // refreshes too — otherwise the chain works exactly once and then silently
    // stops, which is the bug this replaced.
    const backToThis = () => ({ type:"spmt-view", rec:initial, onDone:modal.onDone });
    const refreshPage = () => { if (modal.onDone) modal.onDone(); };
    const reopenAfterChildSave = () => { refreshPage(); setModal(backToThis()); };

    const [f, set] = useFreshForm(initial || blank, "ScheduledPmts");
    const [childRpmts, setChildRpmts] = useState([]);
    const [childLoading, setChildLoading] = useState(false);
    const [spmtTab, setSpmtTab] = useState("current");
    const feeTypeOptions = [
      "", "Administrative Fee","Amendment Fees","Annual Fee","Appraisal","Arranger Fee","Collateral Monitoring Fee","Default Interest","Fee Amortized","Field Exam",
      "Forbearance Fee","Incident Fee","Late Fee","Legal Fees","Multiple (See Notes)","Other","Overadvance Fee","Past Due Sched Int","PIK Offset","Pre-payment penalty",
      "Pre-Petition Interest Due","Structuring Fee","Unused Fee","Up Front Fee"];
    // A saved type that isn't in the list (old-system values like "Legal") still
    // has to show, or the select renders blank and a save would clear it.
    const withType = cur => (cur && !feeTypeOptions.includes(cur)) ? [...feeTypeOptions, cur] : feeTypeOptions;
    // ── Other / Pass-through line items (ScheduledPmtLines) ──
    // A category with lines is owned by them: its Type and Amount fields below
    // are locked and the API sets them from the lines.
    const [pmtLines, setPmtLines] = useState([]);
    const loadLines = () => {
      if (!initial?.id) return;
      api.get("getScheduledPmtLines?scheduledpmtid="+initial.id).then(d=>setPmtLines(d||[])).catch(()=>setPmtLines([]));
    };
    useEffect(loadLines, [initial?.id]); // eslint-disable-line react-hooks/exhaustive-deps
    const otherLines = pmtLines.filter(l=>l.category==="Other");
    const ptLines    = pmtLines.filter(l=>l.category==="PassThrough");
    const afterLinesSaved = (amtKey, typeKey) => header => {
      if (header) { set(amtKey)(header.total); set(typeKey)(header.type); }
      loadLines();
      refreshPage();
    };
    const lockedNote = <div style={{ gridColumn:"3/-1", alignSelf:"end", fontSize:11, color:"var(--color-text-secondary)", paddingBottom:8 }}>Set by line items below.</div>;

    useEffect(()=>{
      if(initial?.id) {
        setChildLoading(true);
        api.get("getReceivedPmts?scheduledpmtid="+initial.id)
          .then(d=>{ setChildRpmts(d); setChildLoading(false); })
          .catch(()=>setChildLoading(false));
      }
    },[initial?.id]);
  
    const totalOrig = (
      (parseFloat(f.origscheduledprincipal)||0) +
      (parseFloat(f.origscheduledinterest)||0) +
      (parseFloat(f.origscheduledfee)||0) +
      (parseFloat(f.origscheduledother)||0) +
      (parseFloat(f.origscheduledpassthrough)||0)
    ).toFixed(2);

    const totalSched = (
      (parseFloat(f.scheduledprincipal)||0) +
      (parseFloat(f.scheduledinterest)||0) +
      (parseFloat(f.scheduledfee)||0) +
      (parseFloat(f.scheduledother)||0) +
      (parseFloat(f.scheduledpassthrough)||0)
    ).toFixed(2);

    const ST = ({ title }) => <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:18, marginBottom:8, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight, gridColumn:"1/-1" }}>{title}</div>;

    return <Modal title={readOnly?"View Scheduled Payment":initial?"Edit Scheduled Payment":"Add Scheduled Payment"} onClose={closeModal} wide 
            onEdit={readOnly&&role.canEdit?()=>{ closeModal(); setModal({type:"spmt-edit",rec:initial,loanid:initial.loanid,onDone:modal.onDone}); }:null}>
      <ReadOnlyForm readOnly={readOnly}>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 12px" }}>
        <Field label="Company Name"><div style={{ padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #ddd", background:"#f5f5f5", color:"#555" }}>{f.companyname||"—"}</div></Field>
        <Field label="Loan Number"><div style={{ padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #ddd", background:"#f5f5f5", color:"#555" }}>{f.loannumber||"—"}</div></Field>
        <Field label="Flags">
          <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer", padding:"7px 0" }}>
            <input type="checkbox" checked={!!f.unscheduledpayment} onChange={e=>set("unscheduledpayment")(e.target.checked)} /> Unscheduled Payment
          </label>
        </Field>
      </div></ReadOnlyForm>

      {/* ── Scheduled payment card ──
          Everything the Save button below writes lives inside this card, so it
          reads as one unit and is clearly apart from the received payments. */}
      <Card style={{ marginTop:8, padding:"6px 16px 16px" }}>
        {/* ── Tab bar ── outside ReadOnlyForm so it stays clickable in view mode */}
        <div style={{ display:"flex", gap:4, marginBottom:16, borderBottom:"1px solid var(--color-border-tertiary)", paddingBottom:0 }}>
          <button onClick={()=>setSpmtTab("current")} style={{ background:"none", border:"none", borderBottom:spmtTab==="current"?"2px solid "+B.cyan:"2px solid transparent", color:spmtTab==="current"?B.cyan:"var(--color-text-secondary)", fontWeight:spmtTab==="current"?600:400, fontSize:13, padding:"6px 16px", cursor:"pointer", marginBottom:"-1px" }}>Current</button>
          {role.label==="Admin"&&<button onClick={()=>setSpmtTab("original")} style={{ background:"none", border:"none", borderBottom:spmtTab==="original"?"2px solid "+B.cyan:"2px solid transparent", color:spmtTab==="original"?B.cyan:"var(--color-text-secondary)", fontWeight:spmtTab==="original"?600:400, fontSize:13, padding:"6px 16px", cursor:"pointer", marginBottom:"-1px" }}>Original</button>}
        </div>

        <ReadOnlyForm readOnly={readOnly}>
        {spmtTab==="current"&&<div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 12px" }}>
          <ST title="General" />
          <Field label="Scheduled Date"><FInput value={f.scheduleddate?.slice(0,10)||""} onChange={set("scheduleddate")} type="date" /></Field>
          <Field label="Status"><div style={{ padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #ddd", background:"#f5f5f5", color:"#555" }}>{f.status||"—"}</div></Field>
          <div />
      
          <ST title="Current Scheduled Amounts" />
          <Field label="Principal"><FInputNum value={f.scheduledprincipal} onChange={set("scheduledprincipal")} /></Field>
          <Field label="Interest"><FInputNum value={f.scheduledinterest} onChange={set("scheduledinterest")} /></Field><div></div> <Field label="PIK"><FInputNum value={f.scheduledpik} onChange={set("scheduledpik")} /></Field>
          <Field label="Fee Type"><FSelect value={f.feetype} onChange={set("feetype")} options={feeTypeOptions} /></Field>
          <Field label="Fee"><FInputNum value={f.scheduledfee} onChange={set("scheduledfee")} /></Field><div></div><div></div>
          <Field label="Other Type"><FSelect value={f.othertype} onChange={set("othertype")} options={withType(f.othertype)} disabled={otherLines.length>0} /></Field>
          <Field label="Other"><FInputNum value={f.scheduledother} onChange={set("scheduledother")} disabled={otherLines.length>0} /></Field>{otherLines.length>0 ? lockedNote : <><div></div><div></div></>}
          {initial?.id && <ScheduledPmtLinesPanel scheduledpmtid={initial.id} category="Other" title="Other"
            lines={otherLines} headerType={f.othertype} headerAmount={f.scheduledother}
            typeOptions={feeTypeOptions} readOnly={readOnly}
            onSaved={afterLinesSaved("scheduledother","othertype")} />}
          <Field label="Pass-through Type"><FSelect value={f.passthroughtype} onChange={set("passthroughtype")} options={withType(f.passthroughtype)} disabled={ptLines.length>0} /></Field>
          <Field label="Pass-through"><FInputNum value={f.scheduledpassthrough} onChange={set("scheduledpassthrough")} disabled={ptLines.length>0} /></Field>
          {ptLines.length>0 ? lockedNote : <><div /><div /></>}
          {initial?.id && <ScheduledPmtLinesPanel scheduledpmtid={initial.id} category="PassThrough" title="Pass-through"
            lines={ptLines} headerType={f.passthroughtype} headerAmount={f.scheduledpassthrough}
            typeOptions={feeTypeOptions} readOnly={readOnly}
            onSaved={afterLinesSaved("scheduledpassthrough","passthroughtype")} />}
          <div /><div /><div />
    <Field label="Total Current Scheduled">
            <div style={{ padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #ddd", background:"#f5f5f5", color:"#333", fontWeight:500 }}>{fmt(totalSched)}</div>
          </Field>
        </div>}

        {spmtTab==="original"&&role.label==="Admin"&&<div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 12px" }}>
          <ST title="General" />
          <Field label="Scheduled Date"><FInput value={f.origscheduleddate?.slice(0,10)||""} onChange={set("origscheduleddate")} type="date" /></Field>
          <ST title="Original Scheduled Amounts" />
          <Field label="Principal"><FInputNum value={f.origscheduledprincipal} onChange={set("origscheduledprincipal")} /></Field>
          <Field label="Interest"><FInputNum value={f.origscheduledinterest} onChange={set("origscheduledinterest")} /></Field><div></div>  
          <Field label="PIK"><FInputNum value={f.origscheduledpik} onChange={set("origscheduledpik")} /></Field>
          <Field label="Fee Type"><FSelect value={f.origfeetype} onChange={set("origfeetype")} options={feeTypeOptions}/></Field>
          <Field label="Fee"><FInputNum value={f.origscheduledfee} onChange={set("origscheduledfee")} /></Field><div></div><div></div>
          <Field label="Other Type"><FSelect value={f.origothertype} onChange={set("origothertype")} options={feeTypeOptions}/></Field>
          <Field label="Other"><FInputNum value={f.origscheduledother} onChange={set("origscheduledother")} /></Field><div></div><div></div>
          <Field label="Pass-through Type"><FSelect value={f.Origpassthroughtype} onChange={set("Origpassthroughtype")} options={feeTypeOptions}/></Field>
          <Field label="Pass-through"><FInputNum value={f.origscheduledpassthrough} onChange={set("origscheduledpassthrough")} /></Field><div></div>
          <Field label="Total Original Scheduled">
            <div style={{ padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #ddd", background:"#f5f5f5", color:"#333", fontWeight:500 }}>{fmt(totalOrig)}</div>
          </Field>
          <div /><div />
        </div>}
      </ReadOnlyForm>
      {!readOnly&&error&&<div style={{ color:"#993C1D",fontSize:12,marginBottom:8,marginTop:8 }}>{error}</div>}
      <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
        <Btn onClick={closeModal}>{readOnly?"Close":"Cancel"}</Btn>
        {!readOnly&&<Btn disabled={saving} onClick={()=>saveRec("saveScheduledPmt",{...f,loanid:f.loanid||loanid,totalorigscheduledpayment:totalOrig,totalscheduledpayment:totalSched},initial?"Edited":"Added","ScheduledPmts",(initial?"Updated":"Added")+" scheduled pmt",modal.onDone)} variant="primary">{saving?"Saving...":"Save"}</Btn>}
      </div>
      </Card>

      {/* ── Child Received Payments ──
          Solid brand-colour rule: a hard break between the scheduled payment
          (card above) and the payments applied against it. */}
      {initial?.id && <>
        <div style={{ marginTop:28, paddingTop:18, borderTop:"3px solid "+B.cyan }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em" }}>Received Payments</div>
              {role.isservicer&&<Btn onClick={()=>{ closeModal(); setModal({
            type:"rpmt-add",
            spmtId:initial.id,
            onDone:reopenAfterChildSave
          }); }} variant="primary">+ Add Payment</Btn>}
          </div>
          {childLoading ? <Spinner /> : childRpmts.length===0
            ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:"8px 0" }}>No received payments for this scheduled payment.</div>
            
          : <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>
                    <Th>Received Date</Th
                    ><Th>Applied Date</Th>
                    <Th>Reversal Date</Th>
                    <Th>Principal</Th>
                    <Th>Interest</Th>
                    <Th>Fee</Th>
                    <Th>Pass-through</Th>
                    <Th>Other</Th>
                    <Th>Total</Th>
                  </tr></thead>
                  <tbody>{childRpmts.map(r=>(
                    <tr key={r.id} style={{ cursor:"pointer" }}
                      onClick={()=>{ closeModal(); setModal({
                        type:"rpmt-view",
                        rec:{...r,id:r.id,scheduledPmtId:r.ScheduledPmtId||r.scheduledpmtid,
                          receiveddate:r.receiveddate,receivedprincipal:r.receivedprincipal,
                          receivedinterest:r.receivedinterest,receivedfee:r.receivedfee,
                          receivedpassthrough:r.receivedpassthrough,receivedother:r.receivedother,
                          reversaldate:r.reversaldate,paymentapplieddate:r.paymentapplieddate,
                          totalreceivedpmt:r.totalreceivedpmt,
                          record_id_deprecated:r.record_id_deprecated},
                        onDone:()=>{
                          // Reload child rpmts after edit
                          setChildLoading(true);
                          api.get("getReceivedPmts?scheduledPmtId="+initial.id)
                            .then(d=>{ setChildRpmts(d); setChildLoading(false); })
                            .catch(()=>setChildLoading(false));
                        }
                      }); }}
                      onMouseEnter={e=>e.currentTarget.style.background="var(--color-background-secondary)"}
                      onMouseLeave={e=>e.currentTarget.style.background=""}>
                      <Td>{fmtDate(r.receiveddate)}</Td>
                      <Td>{fmtDate(r.paymentapplieddate)}</Td>
                      <Td>{fmtDate(r.reversaldate)}</Td>
                      <Td>{fmt(r.receivedprincipal)}</Td>
                      <Td>{fmt(r.receivedinterest)}</Td>
                      <Td>{fmt(r.receivedfee)}</Td>
                      <Td>{fmt(r.receivedpassthrough)}</Td>
                      <Td>{fmt(r.receivedother)}</Td>
                      <Td style={{ fontWeight:500 }}>{fmt(r.totalreceivedpmt)}</Td>
                    <Td><div style={{ display:"flex",gap:6 }} onClick={e=>e.stopPropagation()}>
                      
                      
                      <Btn onClick={()=>{ closeModal(); setModal({
                        type:"rpmt-view",
                        rec:{...r,id:r.id,scheduledPmtId:r.ScheduledPmtId||r.scheduledpmtid,
                          receiveddate:r.receiveddate,receivedprincipal:r.receivedprincipal,
                          receivedinterest:r.receivedinterest,receivedfee:r.receivedfee,
                          receivedpassthrough:r.receivedpassthrough,receivedother:r.receivedother,
                          reversaldate:r.reversaldate,paymentapplieddate:r.paymentapplieddate,
                          totalreceivedpmt:r.totalreceivedpmt,
                          record_id_deprecated:r.record_id_deprecated},
                          returnTo:backToThis()
                          ,
                      }); }}><Icon type="view" /></Btn>
                                            
                      
                      {role.canEdit&& <Btn onClick={()=>{ closeModal(); setModal({
                        type:"rpmt-edit",
                        rec:{...r,id:r.id,scheduledPmtId:r.ScheduledPmtId||r.scheduledpmtid,
                          receiveddate:r.receiveddate,receivedprincipal:r.receivedprincipal,
                          receivedinterest:r.receivedinterest,receivedfee:r.receivedfee,
                          receivedpassthrough:r.receivedpassthrough,receivedother:r.receivedother,
                          reversaldate:r.reversaldate,paymentapplieddate:r.paymentapplieddate,
                          totalreceivedpmt:r.totalreceivedpmt,
                          record_id_deprecated:r.record_id_deprecated},
                          returnTo:backToThis(),
                        onDone:reopenAfterChildSave
                      }); }}><Icon type="edit" /></Btn>}
                      {role.canDelete&&<Btn variant="danger" onClick={e=>{ e.stopPropagation(); setConfirm({
                        msg:"Delete this received payment?",
                        onConfirm:async()=>{
                          await api.post("deleteRecord",{table:"ReceivedPmts",id:r.id});
                          await audit("Deleted","ReceivedPmts",r.id,"Deleted received payment");
                          setConfirm(null);
                          // The page behind holds this payment in its own list.
                          refreshPage();
                          setChildLoading(true);
                          api.get("getReceivedPmts?scheduledPmtId="+initial.id)
                            .then(d=>{ setChildRpmts(d); setChildLoading(false); })
                            .catch(()=>setChildLoading(false));
                        }
                      }); }}><Icon type="delete" /></Btn>}
                    </div></Td>
                  </tr>
                ))}</tbody>
                <tfoot>
                  <tr>
                    <td colSpan={9} style={{ padding:"8px 12px", fontSize:12, fontWeight:600, color:B.cyanDark, textAlign:"right", borderTop:"1px solid "+B.cyanLight }}>
                      Total received:
                    </td>
                    <TdNum style={{ fontWeight:600, color:B.cyanDark, borderTop:"1px solid "+B.cyanLight }}>
                      {fmt(childRpmts.reduce((s,r)=>s+(parseFloat(r.totalreceivedpmt)||0),0))}
                    </TdNum>
                    <td style={{ borderTop:"1px solid "+B.cyanLight }} />
                  </tr>
                </tfoot>
              </table>
            </div>}
        </div>
      </>}

      {initial?.id && <div style={{ gridColumn:"1/-1", marginTop:8 }}>
       <NotesPanel scope="record" entitytype="ScheduledPmt" entityid={initial.id} accountid={initial.accountid} loanid={initial.loanid||loanid} source="Scheduled Pmt" />
       </div>}

    </Modal>;
  };
